import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components/macro';
import clsx from 'clsx';
import { variant, compose } from 'styled-system';
import {
  rem,
  transition,
  themeGetCss,
  safeClick,
  trackingId,
  useFocusVisibleIE,
  themeGetResponsive,
  themeGetAsRem,
  forcedColorsMedia,
  useField,
  useResponsiveCtx,
} from '@blueprint/ui';

import { Text } from '../Typography';

/**
 * Styled input
 */
const Input = styled(Text)(
  themeGetCss('inputs.textInput.base'),
  css`
    /* font-family: inherit; */
    appearance: none;
    border-radius: 0;
    display: flex;
    box-shadow: none;
    background-color: white;
    text-align: left;
    border-width: ${rem(1)};
    border-style: solid;
    padding: ${rem(9)} ${rem(10)} ${rem(10.1)};
    font-size: ${rem(14)};

    ${transition(['border'])};
    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 0.5;
        cursor: not-allowed;
      `}

    &.focus-visible {
      outline: none;
    }

    &:read-only {
      cursor: not-allowed;
    }
    ::placeholder {
      color: #333;
      opacity: 1;
    }
    :-ms-input-placeholder {
      color: #333;
    }
    ::-ms-input-placeholder {
      color: #333;
    }
  `,
  compose(
    variant({ prop: 'inputState', scale: 'inputs.textInput.stateMapping' }),
  ),
  css`
    &.mouseFocus,
    &.mouseFocus:focus {
      outline: none;
    }
    ${({ variant }) =>
      themeGetAsRem(`inputs.textInput.textMapping.${variant}`)};

    line-height: ${(props) => (props.variant === 'large' ? '40px' : 'normal')};
    height: ${(props) => (props.variant === 'large' ? '40px' : 'normal')};
    ${(props) =>
      themeGetAsRem(`inputs.textInput.sizeMapping.${props.fieldSize}`)};
    ${({ vMedia }) =>
      themeGetResponsive({
        vMedia,
        vMediaStyles: css`
          ${themeGetAsRem('inputs.textInput.sizeMapping.medium')};
          ${themeGetAsRem('typography.variantMapping.body2')};
        `,
      })};
    ${forcedColorsMedia()`
      &:disabled {
        opacity: 1;
      }
    `}
  `,
);

/**
 * TextInput
 */
const TextInput = forwardRef((props, ref) => {
  const {
    variant = 'light.large',
    fieldType,
    invalid,
    disabled,
    readOnly,
    spellCheck = true,
    required,
    maxLength,
    className,
    describers,
    name,
    onChange,
    marginBottom = 1,
    value,
    autoComplete = 'off',
    autoCorrect = 'off',
    autoCapitalize = 'off',
    ...rest
  } = useField(props);

  const [mode, size] = variant.split('.');
  const { vMedia } = useResponsiveCtx();
  const inputState = clsx({
    invalid,
    restricted: fieldType === 'restricted',
    uneditable: fieldType === 'uneditable',
  });

  const focusVisibleIE = useFocusVisibleIE(
    { ...rest, className: clsx('TextInput', className) },
    'mouse',
  );

  // handle change event if controlled
  const handleChange = (event) => {
    const value = maxLength
      ? event.target.value.slice(0, maxLength)
      : event.target.value;

    // UPDATE CHANGE HANDLER
    safeClick(onChange, { value, event });
  };

  return (
    <Input
      ref={ref}
      as="input"
      type="text"
      mode={mode}
      disabled={disabled}
      fieldSize={size}
      variant={size}
      inputState={inputState}
      data-testid="TextInput"
      data-trackid={trackingId('TextInput')}
      name={name}
      id={`${name}-field`}
      aria-invalid={invalid}
      aria-readonly={readOnly}
      aria-describedby={describers}
      readOnly={readOnly}
      required={required}
      maxLength={maxLength}
      onChange={onChange ? handleChange : undefined}
      value={value}
      marginBottom={marginBottom}
      autoComplete={autoComplete}
      autoCorrect={autoCorrect}
      autoCapitalize={autoCapitalize}
      spellCheck={spellCheck}
      width="100%"
      {...rest}
      {...focusVisibleIE}
      vMedia={vMedia}
    />
  );
});

TextInput.defaultProps = {
  variant: 'light.large',
  marginBottom: 1,
  spellCheck: true,
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
};

TextInput.displayName = 'TextInput';

export default TextInput;
